import clsx from 'clsx';
import React from 'react';
import { Icon } from 'ui';
import useInputText from './useInputText';

type InputTextProps = {
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  'aria-required'?: boolean;
  className?: string;
  disabled?: boolean;
  icon?: string;
  iconAriaLabel?: string;
  iconColor?: string;
  iconLeft?: boolean;
  iconSize?: string;
  id?: string;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickIcon?: (...args: unknown[]) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  referer?: React.Ref<HTMLInputElement>;
  type?: string;
  /** Decorative-only flag for the inline validation icon. Usually injected by `FormField`. */
  validationState?: 'error' | 'success';
  value?: string;
  variant?: 'default' | 'focus';
};

const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      'aria-label': ariaLabel,
      className,
      disabled = false,
      icon,
      iconAriaLabel,
      iconColor,
      iconLeft = false,
      iconSize = 'l',
      name,
      onChange = () => null,
      onClickIcon,
      onKeyPress = () => {},
      placeholder,
      referer,
      type = 'text',
      validationState,
      value,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const { refInput, getIconAriaLabelValue, isIconClickable } = useInputText({
      disabled,
      icon,
      iconAriaLabel,
      onClickIcon,
      ref,
      referer,
    });

    const iconElement = icon ? <Icon type={icon} size={iconSize} color={iconColor} /> : null;
    const decorativeIcon = icon ? (
      <div className="input-text__icon" aria-hidden="true">
        {iconElement}
      </div>
    ) : null;

    return (
      <div
        className={clsx(
          'input-text',
          `input-text--${variant}`,
          className,
          { 'input-text--icon': !!icon },
          { 'input-text--icon-left': iconLeft },
          { 'input-text--has-validation-icon': !!validationState }
        )}
      >
        <div className="input-text__input-container">
          <input
            name={name}
            className={clsx('input-text__input', { 'input-text__input--disabled': disabled })}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            ref={refInput}
            onKeyPress={onKeyPress}
            aria-label={ariaLabel}
            {...props}
          />
          {icon && isIconClickable ? (
            <button type="button" className="input-text__icon input-text--icon-click" onClick={onClickIcon} aria-label={getIconAriaLabelValue}>
              {iconElement}
            </button>
          ) : (
            decorativeIcon
          )}
          {validationState && (
            <div className="input-text__validation-icon" aria-hidden="true">
              <Icon
                type={validationState === 'error' ? 'caution-outline' : 'check-circle-outline'}
                size="default"
                color={validationState === 'error' ? 'red' : 'success'}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

InputText.displayName = 'InputText';

export default InputText;
